import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { ProductCategoriesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { Badge as CategoryBadge } from './badge'
import { ProductCategoryFilters } from '@/types'
import { Button } from '@/components/base/button'
import { NewDialog } from './new_dialog'
import { useTable } from '@/helpers/useTable'
import { dedupe } from '@/helpers/dedupe'
import { DeleteDialog } from '../delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { useState } from 'react'

interface Props extends TableProps {
  filters?: ProductCategoryFilters
  deleteable?: boolean
}

export const Table = ({ title, pageSize = 20, deleteable = false, fixedHeight = false, filters = {} }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<ProductCategoriesQuery>(
    Query,
    { variables: { filters , first: pageSize } }
  )
  const {
    pageInfo,
    openNew,
    setOpenNew,
    openDelete,
    setOpenDelete
  } = useTable({ pageInfo: data?.productCategories?.pageInfo })
  const [productCategoryId, setProductCategoryId] = useState<string>()

  const deleteProductCategory = (id: string) => {
    if (!deleteable) return

    setProductCategoryId(id)
    setOpenDelete(true)
  }

  if (error) return <Error message={error.message} />
  if (loading || !data) return <Loading />

  return (
    <>
      <BaseTable<ProductCategoryFilters>
        actionButton={<Button color="green" onClick={() => setOpenNew(true)}>Add</Button>}
        fixedHeight={fixedHeight}
        headings={[{ name: 'Name' }, { name: '' }]}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        title={title}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.productCategories.nodes = dedupe([
              ...previousData.productCategories.nodes,
              ...fetchMoreResult.productCategories.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
        ]}
        rows={data.productCategories.nodes.map(pc => (
          {
            id: pc.id,
            columns: [
              <CategoryBadge category={{ color: pc.category.color, name: pc.category.name }} />,
              deleteable
                ? <div className="text-right -mx-1">
                  <Button color="red" onClick={() => deleteProductCategory(pc.id) }>Delete</Button>
                </div>
                : <></>
            ]
          }
        ))}
      />
      { filters.productIdEq && filters.productTypeEq && <NewDialog
        defaultValues={{ productId: filters.productIdEq, productType: filters.productTypeEq }}
        open={openNew}
        setOpen={setOpenNew}
      /> }
      { productCategoryId && (
        <DeleteDialog
          id={productCategoryId}
          mutation={DeleteMutation}
          open={openDelete}
          refetchQueries={[Query]}
          setOpen={setOpenDelete}
          onSuccess={
            () => {
              setOpenDelete(false)
              setProductCategoryId(undefined)
            }
          }
        />)}
    </>
  )
}
